import type { WorkspaceRole } from '@reelplicate/api-client/model';

export type Role = 'owner' | 'editor' | 'reviewer';

/** The API's role (`EDITOR`) as the UI's (`editor`), and back. */
export const fromApiRole = (role: WorkspaceRole) => role.toLowerCase() as Role;
export const toApiRole = (role: Role) => role.toUpperCase() as WorkspaceRole;

/** Roles in order of power (Figma Role Option: "Order by power"). */
export const ROLES: {
  id: Role;
  name: string;
  /** Column header line on Roles & permissions. */
  summary: string;
  /** Role Option line in the invite picker. */
  description: string;
}[] = [
  {
    id: 'owner',
    name: 'Owner',
    summary: 'Everything, including billing.',
    description: 'Everything, including billing and managing the team.',
  },
  {
    id: 'editor',
    name: 'Editor',
    summary: 'Makes drafts and flows.',
    description: 'Makes drafts and flows, edits the brand profile, connects accounts.',
  },
  {
    id: 'reviewer',
    name: 'Reviewer',
    summary: 'Approves drafts.',
    description: 'Approves or rejects drafts before they go out.',
  },
];

/** Roles you can invite someone as - owners are promoted, never invited. */
export const ASSIGNABLE_ROLES = ROLES.filter((r) => r.id !== 'owner');

export const roleName = (role: Role) => ROLES.find((r) => r.id === role)?.name ?? role;

/** Figma "Team — Roles & permissions" (328:2342): who can do what. */
export const PERMISSIONS: { label: string; detail: string; roles: Role[] }[] = [
  {
    label: 'Manage workspace & billing',
    detail: 'Settings, plan, payment, delete workspace',
    roles: ['owner'],
  },
  {
    label: 'Invite & remove people',
    detail: 'Send invites, change roles',
    roles: ['owner'],
  },
  {
    label: 'Create & edit drafts, flows and the brand profile',
    detail: 'Reels, carousels, flows and brand guidelines',
    roles: ['owner', 'editor'],
  },
  {
    label: 'Connect accounts & personas',
    detail: 'TikTok, Instagram, YouTube and the personas that post',
    roles: ['owner', 'editor'],
  },
  {
    label: 'Review drafts',
    detail: 'Approve or reject in the review queue',
    roles: ['owner', 'reviewer'],
  },
  {
    label: 'View analytics',
    detail: 'Dashboards, reports and exports',
    roles: ['owner', 'editor', 'reviewer'],
  },
];
